<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Urlaube
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          ausstehende Urlaubsanfragen
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :officeDropdown="officeDropdown"
            :userDropdown="userDropdown"
            @search="getPendingRequests"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getPendingRequests"/>

        <div class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Mitarbeiter</th>
              <th>Erster Urlaubstag</th>
              <th>Letzter Urlaubstag</th>
              <th class="text-right">Verwendete Urlaubstage</th>
              <th class="text-center">Jahr der verwendeten Urlaubstage</th>
              <th class="text-center">Status</th>
              <th class="text-center">HR Bestätigungsstatus</th>
              <th class="text-center">Standortleiter Bestätigungsstatus</th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">
                {{ $t('general.actions') }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(holiday) in pendingRequests.data" :key="holiday.id">
              <td>{{ holiday?.user?.fullName }}</td>
              <td>{{ holiday?.startDate }}</td>
              <td>{{ holiday?.endDate }}</td>
              <td class="text-right">{{ holiday?.requestDays }}</td>
              <td class="text-center">
                <span v-if="holiday.type === 'regular'">
                   {{ holiday.uniqueYearsFromItems }}
                </span>
                <span v-else>
                   Sonderurlaub
                </span>
              </td>
              <td class="text-center">
                <span v-if="holiday.status === 'pending'">
                   <span class="status-pill yellow p-2 mr-2"
                         data-toggle="tooltip" data-original-title="" title="">
                   </span>
                </span>
                <span v-if="holiday.status === 'approved'">
                    <span class="status-pill green p-2 mr-2"
                          data-toggle="tooltip" data-original-title="" title="">
                   </span>
                </span>
                <span v-if="holiday.status === 'declined'">
                   <span class="status-pill red p-2 mr-2"
                         data-toggle="tooltip" data-original-title="" title="">
                   </span>
                </span>
                {{ holiday.statusTranslated }}
              </td>
              <td class="text-center" style="min-width: 160px">
                <div class="pl-3">
                  <div v-if="holiday.hrApprove === null">
                    <div
                        class="status-pill yellow p-2 mr-2"
                        :title="'Anstehend'"
                        data-toggle="tooltip"
                        data-original-title="">
                    </div>
                    <strong class="js-lists-values-employee-name">Anstehend</strong>
                  </div>
                  <div v-if="holiday.hrApprove === true">
                    <div class="status-pill green p-2 mr-2"
                         data-title="Genehmigt"
                         data-toggle="tooltip" data-original-title="" title="">
                    </div>
                    <strong class="js-lists-values-employee-name">Genehmigt</strong>
                  </div>
                  <div v-if="holiday.hrApprove === false">
                    <div class="status-pill red p-2 mr-2"
                         data-title="Abgelehnt"
                         data-toggle="tooltip" data-original-title="" title="">
                    </div>
                    <strong class="js-lists-values-employee-name">Abgelehnt</strong>
                  </div>
                </div>
              </td>
              <td class="text-center" style="min-width: 160px">
                <div class="pl-3">
                  <div v-if="holiday.coordinatorApprove === null">
                    <div
                        class="status-pill yellow p-2 mr-2"
                        :title="'Anstehend'"
                        data-toggle="tooltip"
                        data-original-title="">
                    </div>
                    <strong class="js-lists-values-employee-name">Anstehend</strong>
                  </div>
                  <div v-if="holiday.coordinatorApprove === true">
                    <div class="status-pill green p-2 mr-2"
                         data-title="Genehmigt"
                         data-toggle="tooltip" data-original-title="" title="">
                    </div>
                    <strong class="js-lists-values-employee-name">Genehmigt</strong>
                  </div>
                  <div v-if="holiday.coordinatorApprove === false">
                    <div class="status-pill red p-2 mr-2"
                         data-title="Abgelehnt"
                         data-toggle="tooltip" data-original-title="" title="">
                    </div>
                    <strong class="js-lists-values-employee-name">Abgelehnt</strong>
                  </div>
                </div>
              </td>
              <td class="text-right">
                <button class="btn btn-primary" @click="router.push({
                name: 'holidayUserOverview', params: {overviewUserId: holiday?.user?.id} })">
                  Übersicht
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="pendingRequests?.pagination?.total_pages > 1"
            :totalPages="pendingRequests?.pagination?.total_pages"
            :currentPage="pendingRequests?.pagination?.current_page"
            @pagechanged="getPendingRequests"/>
      </div>
    </div>
  </PageTemplate>
</template>


<script setup>
import holidayService from '@/api/Holiday';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast } from '@/components/VueToast';
import router from '@/router';
import {
  getOfficeDropdown,
  getUsersForCoordinatorOverviewDropdown,
  getUsersForHrOverviewDropdown,
} from '@/shared/dropdowns';
import { piniaUserStore } from '@/store/piniaUserStore';
import Search from '@/views/App/Holiday/PendingRequest/Search.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t }           = useI18n();
const pendingRequests = ref([]);
const filters         = ref({
  officeId: '',
  userId: '',
  orderBy: 'DESC',
  sortBy: '',
});

const piniaUserState   = piniaUserStore();
const piniaCurrentUser = computed(() => piniaUserState.currentUser.user);
const isSearching      = ref(false);
const isLoaded         = ref(false);
const officeDropdown   = ref([]);
const userDropdown     = ref([]);

async function getPendingRequests(page = 1, perPage, passedFilters) {
  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = pendingRequests.value?.pagination?.items_per_page;

  try {
    const { data } = await holidayService.getPendingRequest({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    pendingRequests.value = data;
    isSearching.value     = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function getUserDropdown() {
  const currentUserAccessRights = piniaCurrentUser.value.userAccessRights;
  if (currentUserAccessRights === 'personal' || currentUserAccessRights === 'head_of_hr') {
    return getUsersForHrOverviewDropdown(userDropdown);
  } else {
    return getUsersForCoordinatorOverviewDropdown(userDropdown);
  }
}

onMounted(async () => {
  CloseToast();
  await Promise.all([
    getUserDropdown(),
    getOfficeDropdown(officeDropdown, t),
    getPendingRequests(),
  ]);

  isLoaded.value = true;
});


</script>

<style scoped>

</style>